import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Skeleton } from '@/common/ui/skeleton'
import { Badge } from '@/common/ui/badge'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import type { BrokeFlexAnalyticsResponse } from '../../types'

interface BrokeFlexAnalyticsTableProps {
	data: BrokeFlexAnalyticsResponse[]
	isLoading?: boolean
}

export default function BrokeFlexAnalyticsTable({
	data,
	isLoading,
}: BrokeFlexAnalyticsTableProps) {
	const formatNumber = (num: number) => {
		return new Intl.NumberFormat('th-TH').format(num)
	}

	const formatCurrency = (num: number) => {
		return new Intl.NumberFormat('th-TH', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(num)
	}

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>รายละเอียดแคมเปญ</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<Skeleton key={i} className="h-16 w-full" />
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	if (data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>รายละเอียดแคมเปญ</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg border border-dashed p-8 text-center">
						<p className="text-muted-foreground">ไม่มีข้อมูลแคมเปญ</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>รายละเอียดแคมเปญ</CardTitle>
				<p className="text-sm text-muted-foreground">
					สถิติของแต่ละแคมเปญ Broke-Flex Message
				</p>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>แคมเปญ</TableHead>
								<TableHead className="text-right">ผู้รับ</TableHead>
								<TableHead className="text-right">Open Rate</TableHead>
								<TableHead className="text-right">Play Rate</TableHead>
								<TableHead className="text-right">Conversions</TableHead>
								<TableHead className="text-right">ยอดฝาก</TableHead>
								<TableHead>อัพเดทล่าสุด</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.map((campaign) => (
								<TableRow key={campaign.id}>
									<TableCell>
										<div>
											<p className="font-medium">
												{campaign.campaignName || 'ไม่มีชื่อ'}
											</p>
											<p className="text-xs text-muted-foreground">
												{campaign.autoMessageTransactionId}
											</p>
										</div>
									</TableCell>
									<TableCell className="text-right">
										{formatNumber(campaign.totalRecipients)}
									</TableCell>
									<TableCell className="text-right">
										<div>
											<p className="font-medium">
												{campaign.openRate?.toFixed(1) || 0}%
											</p>
											<p className="text-xs text-muted-foreground">
												{formatNumber(campaign.uniqueOpeners)} คน
											</p>
										</div>
									</TableCell>
									<TableCell className="text-right">
										<div>
											<p className="font-medium">
												{campaign.playRate?.toFixed(1) || 0}%
											</p>
											<p className="text-xs text-muted-foreground">
												{formatNumber(campaign.uniquePlayers)} คน
											</p>
										</div>
									</TableCell>
									<TableCell className="text-right">
										<Badge variant="secondary">
											{formatNumber(campaign.depositConversions)}
										</Badge>
									</TableCell>
									<TableCell className="text-right">
										<span className="font-medium text-amber-600 dark:text-amber-400">
											฿{formatCurrency(campaign.totalDepositAmount)}
										</span>
									</TableCell>
									<TableCell>
										<p className="text-sm">
											{format(
												new Date(campaign.lastCalculatedAt),
												'dd MMM yyyy',
												{ locale: th },
											)}
										</p>
										<p className="text-xs text-muted-foreground">
											{format(new Date(campaign.lastCalculatedAt), 'HH:mm', {
												locale: th,
											})}
										</p>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	)
}
